/*
  Every SDK and plugin on the website, in one list.

  This file is small on purpose: the sidebar, the hub cards and the page
  headers read it, but a page's actual content (content/<slug>.js) is only
  downloaded when that page is opened.

  Adding an SDK:
    1. Generate its content: node scripts/sdk-docs/md-to-content.mjs <slug> <DOCUMENTATION.md>
    2. Add an entry below.
  The route, sidebar link, hub card, Previous/Next links and AI tools follow.
*/

export const SDK_BASE = '/sdks';

export const CATEGORIES = [
    { id: 'plugin', label: 'No code', title: 'Plugins', blurb: 'Install, add your keys and take payments. No developer needed.' },
    { id: 'server', label: 'Server', title: 'Server SDKs', blurb: 'Run on your backend, where your secret key stays safe.' },
    { id: 'mobile', label: 'Mobile', title: 'Mobile SDKs', blurb: 'Take payments inside your iOS, Android or Flutter app.' },
    { id: 'merchant-app', label: 'Merchant app', title: 'Merchant app SDKs', blurb: 'Build your own merchant app on top of your WayaQuick account.' },
];

export const CATEGORY_BY_ID = Object.fromEntries(CATEGORIES.map((c) => [c.id, c]));

export const SDKS = [
    {
        slug: 'woocommerce',
        name: 'WooCommerce',
        title: 'WooCommerce plugin',
        category: 'plugin',
        runtime: 'WordPress + WooCommerce',
        monogram: 'Wc',
        description: 'Take WayaQuick payments at your WooCommerce checkout. Install, add your keys, done.',
        package: { name: 'wayaquick-for-woocommerce', registry: 'WordPress.com', url: 'https://wordpress.com/plugins/wayaquick-for-woocommerce' },
        load: () => import('./content/woocommerce'),
    },
    {
        slug: 'php',
        name: 'PHP',
        title: 'PHP SDK',
        category: 'server',
        runtime: 'PHP 8.1+',
        monogram: 'PHP',
        description: 'Collections, payouts, account checks, BVN and signed webhooks for PHP 8.1+.',
        package: { name: 'wayaquick/payment-sdk', registry: 'Packagist', url: 'https://packagist.org/packages/wayaquick/payment-sdk' },
        aiContext: true,
        load: () => import('./content/php'),
    },
    {
        slug: 'nodejs',
        name: 'Node.js',
        title: 'Node.js & TypeScript SDK',
        category: 'server',
        runtime: 'Node.js 18+',
        monogram: 'JS',
        description: 'Typed SDK for Node.js and TypeScript with zero runtime dependencies.',
        package: { name: 'wayaquick-intergration', registry: 'npm', url: 'https://www.npmjs.com/package/wayaquick-intergration' },
        aiContext: true,
        load: () => import('./content/nodejs'),
    },
    {
        slug: 'java',
        name: 'Java',
        title: 'Java SDK',
        category: 'server',
        runtime: 'Java 17+',
        monogram: 'Jv',
        description: 'Immutable, thread-safe client built on the JDK HTTP client. Maven and Gradle.',
        package: { name: 'io.github.waya-multi-link:wayaquick-integration', registry: 'Maven' },
        aiContext: true,
        load: () => import('./content/java'),
    },
    {
        slug: 'dotnet',
        name: '.NET',
        title: '.NET SDK',
        category: 'server',
        runtime: '.NET 8',
        monogram: 'C#',
        description: 'Singleton-friendly client for .NET 8 with ASP.NET Core dependency injection.',
        package: { name: 'WayaQuick.Integration', registry: 'NuGet', url: 'https://www.nuget.org/packages/WayaQuick.Integration' },
        aiContext: true,
        load: () => import('./content/dotnet'),
    },
    {
        slug: 'go',
        name: 'Go',
        title: 'Go SDK',
        category: 'server',
        runtime: 'Go 1.20+',
        monogram: 'Go',
        description: 'Standard-library-only module, safe to share across goroutines.',
        package: {
            name: 'github.com/WAYA-MULTI-LINK/…/wayaquick',
            registry: 'GitHub',
            url: 'https://github.com/WAYA-MULTI-LINK/WAYAPAYCHAT-1.0-GOLANG-DOCUMENTATION',
        },
        aiContext: true,
        load: () => import('./content/go'),
    },
    {
        slug: 'ios',
        name: 'iOS',
        title: 'iOS SDK',
        category: 'mobile',
        runtime: 'iOS 14+ · Swift 5.9',
        monogram: 'iOS',
        description: 'Pure Swift SDK with async/await. Payments, payouts and account checks.',
        package: { name: 'WAYA-PAY-CHAT-2.0-IOS-SDK', registry: 'GitHub', url: 'https://github.com/WAYA-MULTI-LINK/WAYA-PAY-CHAT-2.0-IOS-SDK' },
        load: () => import('./content/ios'),
    },
    {
        slug: 'android',
        name: 'Android',
        title: 'Android SDK',
        category: 'mobile',
        runtime: 'Android · Java',
        monogram: 'An',
        description: 'Android library with blocking and async calls for payments and payouts.',
        load: () => import('./content/android'),
    },
    {
        slug: 'flutter',
        name: 'Flutter',
        title: 'Flutter SDK',
        category: 'mobile',
        runtime: 'Flutter 3 · Dart 3',
        monogram: 'Fl',
        description: 'One Dart SDK for payments and payouts on Android and iOS.',
        load: () => import('./content/flutter'),
    },
    {
        slug: 'ionic',
        name: 'Ionic',
        title: 'Ionic merchant app SDK',
        category: 'merchant-app',
        runtime: 'Capacitor 5+',
        monogram: 'Io',
        description: 'Headless TypeScript client for Angular, React or Vue Ionic apps.',
        load: () => import('./content/ionic'),
    },
    {
        slug: 'react-native',
        name: 'React Native',
        title: 'React Native merchant app SDK',
        category: 'merchant-app',
        runtime: 'React Native',
        monogram: 'RN',
        description: 'Headless client with secure keychain storage for React Native apps.',
        load: () => import('./content/react-native'),
    },
    {
        slug: 'flutter-app',
        name: 'Flutter (merchant app)',
        title: 'Flutter merchant app SDK',
        category: 'merchant-app',
        runtime: 'Flutter · Dart',
        monogram: 'Fl',
        description: 'Headless Dart client that works with Riverpod, Bloc, GetX or setState.',
        load: () => import('./content/flutter-app'),
    },
];

export const SDK_BY_SLUG = Object.fromEntries(SDKS.map((s) => [s.slug, s]));

export const sdkPath = (slug) => `${SDK_BASE}/${slug}`;

// Previous / Next across the whole docs flow: hub → each SDK → Standard API.
export function neighbours(slug) {
    const i = SDKS.findIndex((s) => s.slug === slug);
    const prev = i <= 0 ? { to: SDK_BASE, label: 'SDKs & Plugins' } : { to: sdkPath(SDKS[i - 1].slug), label: SDKS[i - 1].title };
    const next =
        i === SDKS.length - 1
            ? { to: '/standard-api-integration', label: 'Standard API Integration' }
            : { to: sdkPath(SDKS[i + 1].slug), label: SDKS[i + 1].title };
    return { prev, next };
}

// Old addresses: /web-integration#php, /mobile-integration#Flutter, /plugin-sdk#net …
const OLD_ANCHORS = {
    web: { woocommerce: 'woocommerce', php: 'php', net: 'dotnet', dotnet: 'dotnet', java: 'java', nodejs: 'nodejs', go: 'go', ionic: 'ionic', reactnative: 'react-native', flutter: 'flutter-app' },
    mobile: { ios: 'ios', android: 'android', flutter: 'flutter' },
};

export function legacyTarget(kind, hash) {
    const key = decodeURIComponent(String(hash || '').replace(/^#/, '')).toLowerCase().replace(/[^a-z0-9]/g, '');
    const slug = (OLD_ANCHORS[kind] || OLD_ANCHORS.web)[key];
    if (slug) return sdkPath(slug);
    if (key === 'apikeys') return `${SDK_BASE}#API%20Keys`;
    if (kind === 'mobile') return `${SDK_BASE}?type=mobile`;
    return SDK_BASE;
}
